import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { geofenceSetupOf } from "../../domain/geofenceReminder";
import { toEpochSeconds } from "../../domain/time";
import { buildLocalTimeline, localClockState } from "../../offline/localTimeline";
import { useClockSnapshot, useLocalEvents, useOffline } from "../../offline/OfflineContext";
import { usePlatform } from "../../platform/PlatformContext";
import { useOwnerKey } from "../../shared/session/tenantSelection";
import { useWorkTimeZone } from "../../shared/session/useWorkTimeZone";
import { useNow } from "../../shared/ui/useNow";
import { useGeofenceReminder } from "./useGeofenceReminder";

/** How often the setup follows the passing periods while the app is open. */
const refreshMs = 15 * 60_000;

/**
 * Keeps the geofence reminder of the device in line with the consent of the person, the release of
 * the tenant, the periods of due work and whether the person is clocked in (spec 12.4, T-188,
 * ADR-0167). Without consent, after revocation, without the release or after an error answer of
 * the server every region, alarm and reminder is removed. Nothing is sent to the server; works
 * offline with the setup kept on the device. Renders nothing.
 *
 * @returns `null`.
 */
function DeviceSync(): null {
  const platform = usePlatform();
  const offline = useOffline();
  const owner = useOwnerKey();
  const control = useGeofenceReminder(owner);
  const events = useLocalEvents(owner);
  const snapshot = useClockSnapshot(owner);
  const workTimeZone = useWorkTimeZone();
  const nowMs = useNow(offline.nowMs, refreshMs);
  const [resumes, setResumes] = useState(0);
  const sent = useRef<string | null>(null);
  const reminders = control.reminders;
  const answer = control.answer;
  const consented = control.consent !== null;
  const timeZone = workTimeZone ?? offline.deviceTimeZone;
  const clockedIn =
    !events.loaded || snapshot === undefined
      ? null
      : localClockState(
          buildLocalTimeline(snapshot, events.events, toEpochSeconds(nowMs), timeZone),
        ).state !== "Off";
  const off = control.consentLoaded && (!consented || control.refused || answer?.enabled === false);
  const setup =
    reminders === null || off || !consented || answer === null || clockedIn === null
      ? null
      : geofenceSetupOf(answer, clockedIn, nowMs, reminders.oneShotRegions);
  const key = off ? `off:${String(resumes)}` : setup === null ? null : JSON.stringify(setup);

  // A reminder of iOS fires once: back in the foreground the regions are armed again.
  useEffect(() => {
    const unsubscribe = platform.onResume(() => {
      setResumes((count) => count + 1);
      sent.current = null;
    });

    return unsubscribe;
  }, [platform]);

  useEffect(() => {
    if (reminders === null || key === null || key === sent.current) {
      return;
    }

    sent.current = key;

    if (setup === null) {
      reminders.clear().catch(() => undefined);
    } else {
      reminders.configure(setup).catch(() => undefined);
    }
  }, [reminders, key, setup]);

  return null;
}

/**
 * Mounts the synchronization only on a device with the geofence reminder, so the browser and the
 * installed PWA do no work for it (T-188).
 *
 * @returns The synchronization, or `null`.
 */
export function GeofenceReminderSync(): JSX.Element | null {
  const platform = usePlatform();
  const sync = platform.geofence === undefined ? null : <DeviceSync />;

  return sync;
}
